import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { PrezemInput } from "components/ui/floating-input";
import { PrezemButton } from "components/ui/button";
import Textarea from "elements/v2/Textarea/Textarea";
import AdminDatePicker from "elements/v2/AdminDatePicker/AdminDatePicker";
import { VisuallyHiddenInput } from "elements/v2/VisuallyHiddenInput/VisuallyHiddeninput";
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  Checkbox, 
  FormControlLabel,
  Divider,
  Card,
  CardContent,
  IconButton
} from "@mui/material";
import { CloudUpload, Delete, Add } from "@mui/icons-material";
import dayjs, { Dayjs } from "dayjs";
import CategorySelects from "../../PopupStores/Command/components/CategorySelect";
import { SelectChangeEvent } from "@mui/material";
import { CreateOnlinePopupStore, FetchOnlinePopupStoreDetail, UpdateOnlinePopupStore } from "services/online-popup-stores.service";
import { GetPresignedUrl, PresignedUrl, UploadFileUsingPresignedUrl } from "services/uploads.service";
import Swal from "sweetalert2";
import { useEffect } from "react";

interface ProductInfo {
  id: string;
  productImage: string;
  productName: string;
  originPrice: string;
  discountPrice: string;
  hasDiscount: boolean;
  discountInfo: string;
  productLink: string;
}

export default function CommandOnlinePopupStores() {
  const params = useParams();
  const navigate = useNavigate();

  // 기본 정보 상태
  const [popupName, setPopupName] = useState("");
  const [slug, setSlug] = useState("");
  const [category, setCategory] = useState<string[]>([]);
  const [operatingStartDate, setOperatingStartDate] = useState<Dayjs | null>(null);
  const [operatingEndDate, setOperatingEndDate] = useState<Dayjs | null>(null);
  const [playTime, setPlayTime] = useState("");
  const [eventTitle, setEventTitle] = useState("");
  const [eventInfo, setEventInfo] = useState("");
  const [thumbnailImages, setThumbnailImages] = useState<string[]>([]);
  
  // 팝업 소개 상태
  const [introTitle, setIntroTitle] = useState("");
  const [introContent, setIntroContent] = useState("");
  
  // 제품 정보 상태 (API 스키마에 없는 필드들 제거)
  const [products, setProducts] = useState<ProductInfo[]>([]);
  
  // CTA 버튼 상태
  const [preOpenButtonText, setPreOpenButtonText] = useState("");
  const [postOpenButtonText, setPostOpenButtonText] = useState("");
  const [postCloseButtonText, setPostCloseButtonText] = useState("");

  // 상태 추가
  const [productBannerImageUrl, setProductBannerImageUrl] = useState("");
  const [showAllLink, setShowAllLink] = useState("");
  const productBannerInputRef = React.useRef<HTMLInputElement>(null);
  const triggerProductBannerUpload = () => {
    productBannerInputRef.current?.click();
  };
  const [isEventActive, setIsEventActive] = useState(false);
  const [isProductActive, setIsProductActive] = useState(false);

  // 파일 업로드 처리 (PresignedURL 방식)
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>, callback: (urls: string[]) => void) => {
    const files = event.target.files;
    if (!files || files.length === 0) {
      return;
    }

    const urls: string[] = [];
    Swal.showLoading();
    
    try {
      for (const file of Array.from(files)) {
        const presignedUrl: PresignedUrl = await GetPresignedUrl();

        const isSuccess: boolean = await UploadFileUsingPresignedUrl({
          url: `https://s3.ap-northeast-2.amazonaws.com/${presignedUrl.fields.bucket}`,
          'X-Amz-Algorithm': presignedUrl.fields['X-Amz-Algorithm'],
          'X-Amz-Credential': presignedUrl.fields['X-Amz-Credential'],
          'X-Amz-Signature': presignedUrl.fields['X-Amz-Signature'],
          'X-Amz-Date': presignedUrl.fields['X-Amz-Date'],
          bucket: presignedUrl.fields.bucket,
          key: presignedUrl.fields.key,
          Policy: presignedUrl.fields.Policy,
          'Content-Type': file.type,
          file,
        });

        if (!isSuccess) {
          Swal.fire('이미지 업로드 실패', '이미지 업로드에 실패했습니다. 다시 시도해주세요.', 'error');
          return;
        }

        // uploadedUrl 저장 (기존 방식과 동일)
        urls.push(presignedUrl.uploadedUrl);
      }
      
      Swal.close();
      callback(urls);
    } catch (error) {
      Swal.close();
      Swal.fire('이미지 업로드 실패', '이미지 업로드에 실패했습니다. 다시 시도해주세요.', 'error');
    }
  };

  // 파일 입력 ref들
  const thumbnailInputRef = React.useRef<HTMLInputElement>(null);
  const productImageInputRefs = React.useRef<{ [key: string]: HTMLInputElement | null }>({});

  // 파일 선택 트리거 함수들
  const triggerThumbnailUpload = () => {
    thumbnailInputRef.current?.click();
  };

  const triggerProductImageUpload = (productId: string) => {
    productImageInputRefs.current[productId]?.click();
  };



  // 썸네일 이미지 업로드 처리
  const handleThumbnailUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    await handleFileUpload(event, (urls) => {
      setThumbnailImages(prev => [...prev, ...urls].slice(0, 10)); // 최대 10장 제한
    });
  };

  // 제품 추가
  const addProduct = () => {
    const newProduct: ProductInfo = {
      id: Date.now().toString(),
      productImage: "",
      productName: "",
      originPrice: "",
      discountPrice: "",
      hasDiscount: false,
      discountInfo: "",
      productLink: ""
    };
    setProducts([...products, newProduct]);
  };

  // 제품 삭제
  const removeProduct = (productId: string) => {
    setProducts(products.filter(p => p.id !== productId));
  };

  // 제품 정보 업데이트
  const updateProduct = (productId: string, field: keyof ProductInfo, value: any) => {
    setProducts(products.map(p => 
      p.id === productId ? { ...p, [field]: value } : p
    ));
  };

  // 썸네일 이미지 삭제
  const removeThumbnail = (index: number) => {
    setThumbnailImages(prev => prev.filter((_, i) => i !== index));
  };

  // 카테고리 선택 처리
  const handleCategories = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value as string[];
    setCategory(value);
  };

  // 상세 데이터 로딩
  const loadOnlinePopupStoreDetail = async (id: string) => {
    try {
      const data = await FetchOnlinePopupStoreDetail(Number(id));
      if (data) {
        // console.log(data.actionButtons);
        setPopupName(data.name || "");
        setSlug(data.slug || "");
        setCategory(data.categories || []);
        setOperatingStartDate(data.startDate ? dayjs(data.startDate) : null);
        setOperatingEndDate(data.endDate ? dayjs(data.endDate) : null);
        setPlayTime(data.playTime?.toString() || "");
        setEventTitle(data.eventTitle || "");
        setEventInfo(data.eventDescription || "");
        setThumbnailImages(data.thumbnailImages || []);
        setIntroTitle(data.introTitle || "");
        setIntroContent(data.introContent || "");
        setPreOpenButtonText(data.actionButtons?.preOpen || "");
        setPostOpenButtonText(data.actionButtons?.inProgress || "");
        setPostCloseButtonText(data.actionButtons?.closed || "");
        setProductBannerImageUrl(data.productBannerImageUrl || "");
        setShowAllLink(data.showAllLink || "");
        
        // 제품 데이터 설정
        if (data.products && data.products.length > 0) {
          const mappedProducts = data.products.map((product: any, index: number) => ({
            id: (index + 1).toString(),
            productImage: product.imageUrl || "",
            productName: product.name || "",
            originPrice: product.originPrice?.toString() || "",
            discountPrice: product.discountPrice?.toString() || "",
            hasDiscount: product.isDiscounted || false,
            discountInfo: product.discountInfo || "",
            productLink: product.link || ""
          }));
          setProducts(mappedProducts);
        }

        // 상세 데이터 로딩 시 반영
        if (data.eventTitle || data.eventDescription) {
          setIsEventActive(true);
        } else {
          setIsEventActive(false);
        }
        // 상세 데이터 로딩 시 반영
        if (data.products && data.products.length > 0) {
          setIsProductActive(true);
        } else {
          setIsProductActive(false);
        }
      }
    } catch (error) {
      console.error('상세 정보 로딩 실패:', error);
      Swal.fire('오류', '상세 정보를 불러오는데 실패했습니다.', 'error');
    }
  };

  // 컴포넌트 마운트 시 상세 데이터 로딩
  useEffect(() => {
    if (params.id && params.id !== 'new') {
      loadOnlinePopupStoreDetail(params.id);
    }
  }, [params.id]);

  // 폼 제출
  const handleSubmit = async () => {
    try {
      // 유효성 검사
      if (!popupName.trim()) {
        await Swal.fire('오류', '팝업명을 입력해주세요.', 'error');
        return;
      }
      
      if (!slug.trim()) {
        await Swal.fire('오류', 'Slug를 입력해주세요.', 'error');
        return;
      }
      
      if (category.length === 0) {
        await Swal.fire('오류', '카테고리를 선택해주세요.', 'error');
        return;
      }

      if (!operatingStartDate || !operatingEndDate) {
        await Swal.fire('오류', '운영 기간을 설정해주세요.', 'error');
       
        return;
      }
      if (products.some(p => !p.productName.trim() || !p.originPrice.trim())) {
        await Swal.fire('오류', '모든 제품의 이름과 가격을 입력해주세요.', 'error');
        return;
      }

      // API 스키마에 맞는 데이터 변환
      const apiData = {
        name: popupName,
        slug: slug,
        categories: category,
        thumbnailImages: thumbnailImages,
        startDate: operatingStartDate.format("YYYY-MM-DD"),
        endDate: operatingEndDate.format("YYYY-MM-DD"),
        playTime: parseInt(playTime) || 0,
        eventTitle: eventTitle,
        eventDescription: eventInfo,
        introTitle: introTitle,
        introContent: introContent,
        preOpenButtonText: preOpenButtonText || "",
        inProgressButtonText: postOpenButtonText || "",
        closedButtonText: postCloseButtonText || "",
        productBannerImageUrl,
        showAllLink,
        products: products.map(product => ({
          name: product.productName,
          imageUrl: product.productImage,
          isDiscounted: product.hasDiscount,
          discountInfo: product.hasDiscount ? product.discountInfo : undefined,
          originPrice: parseInt(product.originPrice) || 0,
          discountPrice: product.hasDiscount ? parseInt(product.discountPrice) || 0 : undefined,
          link: product.productLink
        }))
      };
      
      console.log("API 데이터:", apiData);
      
      // 폼 제출 시 이벤트 정보 비활성화면 값 비움
      if (!isEventActive) {
        apiData.eventTitle = "";
        apiData.eventDescription = "";
      }

      // 폼 제출 시 제품 정보 비활성화면 값 비움
      if (!isProductActive) {
        apiData.products = [];
      }

      // API 호출 (등록 또는 수정)
      const isEditMode = params.id && params.id !== 'new';
      if (isEditMode) {
        // 수정 모드: 모든 필드를 포함하여 전송 (빈 값도 포함)
        const updateData: any = {};
        updateData.name = apiData.name;
        updateData.slug = apiData.slug;
        updateData.categories = apiData.categories;
        updateData.thumbnailImages = apiData.thumbnailImages;
        updateData.startDate = apiData.startDate;
        updateData.endDate = apiData.endDate;
        updateData.playTime = apiData.playTime;
        updateData.eventTitle = apiData.eventTitle;
        updateData.eventDescription = apiData.eventDescription;
        updateData.introTitle = apiData.introTitle;
        updateData.introContent = apiData.introContent;
        updateData.preOpenButtonText = apiData.preOpenButtonText;
        updateData.inProgressButtonText = apiData.inProgressButtonText;
        updateData.closedButtonText = apiData.closedButtonText;
        updateData.productBannerImageUrl = apiData.productBannerImageUrl;
        updateData.showAllLink = apiData.showAllLink;
        updateData.products = apiData.products;
        
        await UpdateOnlinePopupStore(Number(params.id), updateData);
      } else {
        await CreateOnlinePopupStore(apiData);
      }
      
      // 성공 처리
      await Swal.fire('성공', `온라인 팝업스토어가 성공적으로 ${isEditMode ? '수정' : '등록'}되었습니다.`, 'success');
      navigate('/online-popup-stores');
      
    } catch (error) {
      console.error('등록 실패:', error);
      await Swal.fire('오류', '등록 중 오류가 발생했습니다. 다시 시도해주세요.', 'error');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {params.id && params.id !== 'new' ? '온라인 팝업스토어 수정' : '온라인 팝업스토어 등록'}
      </Typography>
      
      <Grid container spacing={3}>
        {/* 기본 정보 섹션 */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom color="primary">
              기본 정보
            </Typography>
            <Grid container spacing={2}>
              {/* 첫 번째 줄: 팝업명, Slug, 플레이 시간 */}
              <Grid item xs={12} md={4}>
                <PrezemInput
                  label="팝업명"
                  required
                  value={popupName}
                  onChange={(e) => setPopupName(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <PrezemInput
                  label="Slug"
                  required
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <PrezemInput
                  label="플레이 시간 (분)"
                  type="number"
                  value={playTime}
                  onChange={(e) => setPlayTime(e.target.value)}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  분 단위로 입력해주세요
                </Typography>
              </Grid>
              
              {/* 두 번째 줄: 카테고리, 운영 기간 */}
              <Grid item xs={12} md={4}>
                <CategorySelects
                  categories={category}
                  handleCategories={handleCategories}
                  fullWidth={true}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <AdminDatePicker
                  label="운영 시작일"
                  required
                  value={operatingStartDate}
                  onChange={setOperatingStartDate}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <AdminDatePicker
                  label="운영 종료일"
                  required
                  value={operatingEndDate}
                  onChange={setOperatingEndDate}
                  minDate={operatingStartDate}
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ mr: 2 }}>
                      이벤트 정보 (온라인 팝업 해택)
                    </Typography>
                    <FormControlLabel
                      control={<Checkbox checked={isEventActive} onChange={e => setIsEventActive(e.target.checked)} />}
                      label="이벤트 정보 입력 활성화"
                    />
                  </Box>
                  {isEventActive && (
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <PrezemInput
                          label="이벤트 제목"
                          value={eventTitle}
                          onChange={(e) => setEventTitle(e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Textarea
                          label="이벤트 정보"
                          minRows={3}
                          value={eventInfo}
                          onChange={(e) => setEventInfo(e.target.value)}
                        />
                      </Grid>
                    </Grid>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    썸네일 이미지
                  </Typography>
                  <Box>
                    <PrezemButton variant="outline" onClick={triggerThumbnailUpload}>
                      <CloudUpload sx={{ mr: 1 }} />
                      이미지 추가 (최대 10장 / 390x410 사이즈)
                    </PrezemButton>
                    <VisuallyHiddenInput
                      ref={thumbnailInputRef}
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleThumbnailUpload}
                    />
                  </Box>
                                      {thumbnailImages.length > 0 && (
                      <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {thumbnailImages.map((imageUrl, index) => (
                          <Box key={index} sx={{ position: 'relative', width: 100, height: 100 }}>
                            <img
                              src={imageUrl}
                              alt={`썸네일 ${index + 1}`}
                              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 4 }}
                            />
                            <IconButton
                              size="small"
                              sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'error.main', color: 'white' }}
                              onClick={() => removeThumbnail(index)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    )}
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* 팝업 소개 섹션 */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom color="primary">
              팝업 소개
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <PrezemInput
                  label="팝업 소개 제목"
                  value={introTitle}
                  onChange={(e) => setIntroTitle(e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <Textarea
                  label="팝업 소개 내용"
                  minRows={5}
                  value={introContent}
                  onChange={(e) => setIntroContent(e.target.value)}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* 제품 정보 섹션 */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6" gutterBottom color="primary" sx={{ mr: 2 }}>
                제품 정보 ✅
              </Typography>
              <FormControlLabel
                control={<Checkbox checked={isProductActive} onChange={e => setIsProductActive(e.target.checked)} />}
                label="제품 정보 입력 활성화"
              />
            </Box>
            {isProductActive && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  제품별로 이미지, 이름, 가격 정보를 입력해주세요.
                </Typography>
                <Divider sx={{ my: 3 }} />
                <Grid container spacing={2} alignItems="center" mb={2}>
                  <Grid item xs={12} md={6}>
                    <Box>
                      <PrezemButton variant="outline" style={{ width: '100%' }} onClick={triggerProductBannerUpload}>
                        <CloudUpload sx={{ mr: 1 }} />
                        제품 배너 이미지 업로드 (권장 350x140)
                      </PrezemButton>
                      <VisuallyHiddenInput
                        ref={productBannerInputRef}
                        type="file"
                        accept="image/*"
                        onChange={async (e) => await handleFileUpload(e, ([url]) => setProductBannerImageUrl(url))}
                      />
                      {productBannerImageUrl && (
                        <Box sx={{ mt: 2, position: 'relative', display: 'inline-block' }}>
                          <img
                            src={productBannerImageUrl}
                            alt="제품 배너"
                            style={{ width: '100%', maxWidth: 350, height: 140, objectFit: 'cover', borderRadius: 4 }}
                          />
                          <IconButton
                            size="small"
                            sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'error.main', color: 'white' }}
                            onClick={() => setProductBannerImageUrl("")}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <PrezemInput
                      label="제품 전체보기 링크"
                      value={showAllLink}
                      onChange={(e) => setShowAllLink(e.target.value)}
                    />
                    <Typography variant="caption" color="text.secondary">
                      전체보기 클릭 시 랜딩될 링크 입력
                    </Typography>
                  </Grid>
                </Grid>

                {/* 제품 목록 */}
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle1">제품 정보</Typography>
                  <PrezemButton
                    variant="outline"
                    onClick={addProduct}
                  >
                    <Add sx={{ mr: 1 }} />
                    제품 추가
                  </PrezemButton>
                </Box>

                {products.map((product, index) => (
                  <Card key={product.id} sx={{ mb: 2 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">제품 {index + 1}</Typography>
                        <IconButton
                            color="error"
                            onClick={() => removeProduct(product.id)}
                          >
                          <Delete />
                        </IconButton>
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                                              <Box>
                            <PrezemButton variant="outline" style={{ width: '100%' }} onClick={() => triggerProductImageUpload(product.id)}>
                              <CloudUpload sx={{ mr: 1 }} />
                              썸네일 이미지 (png / 1:1 사이즈 권장)
                            </PrezemButton>
                            <VisuallyHiddenInput
                              ref={(el) => {
                                productImageInputRefs.current[product.id] = el;
                              }}
                              type="file"
                              accept="image/*"
                              onChange={async (e) => await handleFileUpload(e, ([url]) => updateProduct(product.id, 'productImage', url))}
                            />
                          </Box>
                                              {product.productImage && (
                            <Box sx={{ mt: 2, position: 'relative', display: 'inline-block' }}>
                              <img
                                src={product.productImage}
                                alt="제품 이미지"
                                style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 4 }}
                              />
                              <IconButton
                                size="small"
                                sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'error.main', color: 'white' }}
                                onClick={() => updateProduct(product.id, 'productImage', "")}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Box>
                          )}
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <PrezemInput
                                label="제품명"
                                value={product.productName}
                                onChange={(e) => updateProduct(product.id, 'productName', e.target.value)}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <PrezemInput
                                label="원가격"
                                value={product.originPrice}
                                onChange={(e) => updateProduct(product.id, 'originPrice', e.target.value)}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <PrezemInput
                                label="제품 링크"
                                value={product.productLink}
                                onChange={(e) => updateProduct(product.id, 'productLink', e.target.value)}
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item xs={12}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={product.hasDiscount}
                                onChange={(e) => updateProduct(product.id, 'hasDiscount', e.target.checked)}
                              />
                            }
                            label="할인 적용 여부"
                          />
                        </Grid>
                        {product.hasDiscount && (
                          <>
                            <Grid item xs={12} md={6}>
                              <PrezemInput
                                label="할인 명"
                                value={product.discountInfo}
                                onChange={(e) => updateProduct(product.id, 'discountInfo', e.target.value)}
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <PrezemInput
                                label="할인 가격"
                                value={product.discountPrice}
                                onChange={(e) => updateProduct(product.id, 'discountPrice', e.target.value)}
                              />
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </CardContent>
                  </Card>
                ))}
              </>
            )}
          </Paper>
        </Grid>

        {/* CTA 버튼 섹션 */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom color="primary">
              CTA 버튼
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <PrezemInput
                  label="오픈 전"
                  value={preOpenButtonText}
                  onChange={(e) => setPreOpenButtonText(e.target.value)}
                />
                <Typography variant="caption" color="text.secondary">
                  *클릭 시 팝업 시작
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <PrezemInput
                  label="오픈 후"
                  value={postOpenButtonText}
                  onChange={(e) => setPostOpenButtonText(e.target.value)}
                />
                <Typography variant="caption" color="text.secondary">
                  *클릭 시 팝업 시작
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <PrezemInput
                  label="팝업 종료"
                  value={postCloseButtonText}
                  onChange={(e) => setPostCloseButtonText(e.target.value)}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* 제출 버튼 */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <PrezemButton
              variant="outline"
              size="lg"
              onClick={() => window.history.back()}
            >
              취소
            </PrezemButton>
            <PrezemButton
              size="lg"
              onClick={handleSubmit}
            >
              {params.id && params.id !== 'new' ? '수정하기' : '등록하기'}
            </PrezemButton>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
